import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { getStandardDialogPaperSx, standardDialogBackdropSx } from "./StandardDialog";
import AuthLegalStrip from "./Auth/AuthLegalStrip";
import {
  galleryInquiryBodySx,
  galleryInquiryHeaderInsetSx,
  galleryInquirySuccessIconWellSx,
  galleryInquiryTitleSx,
  getGalleryInquiryCloseButtonSx,
  getGalleryInquiryDialogSx,
  getGalleryInquiryEmailFieldSx,
  getGalleryInquiryPaperSx,
  getGalleryInquiryPrimaryFullWidthCtaSx,
} from "./galleryInquiryModalStyles";
import { normalizeEmail } from "../utils/sessionAudience";
import { ReactComponent as UploadCompleteIcon } from "../Assets/upload-complete.svg";

const CONTACT_DIALOG_TITLE_ID = "gallery-uploader-contact-modal-title";

/** Matches the API's cap so a label can't be silently truncated server-side. */
const MAX_DISPLAY_NAME_CHARS = 200;

function contactEmailErrorMessage(value) {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) return "Enter your email";
  if (!normalizeEmail(trimmed)) return "Enter a valid email address";
  return "";
}

export default function GalleryUploaderContactModal({
  open,
  onClose,
  accountEmail,
  savedContactEmail,
  savedInquiryDisplayName,
  onSubmit,
  submitting,
  saved,
  error,
}) {
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [displayName, setDisplayName] = useState("");
  const primaryFullWidthCtaSx = getGalleryInquiryPrimaryFullWidthCtaSx(theme);
  const emailFieldSx = getGalleryInquiryEmailFieldSx(theme);
  const closeButtonSx = getGalleryInquiryCloseButtonSx(theme);
  /** Autofocus would open the soft keyboard over the CTA and legal strip. */
  const focusEmailOnOpen = useMediaQuery(theme.breakpoints.up("sm"));

  useEffect(() => {
    if (!open) return;
    setEmail(savedContactEmail || accountEmail || "");
    setEmailError("");
    setDisplayName(savedInquiryDisplayName || "");
  }, [open, savedContactEmail, accountEmail, savedInquiryDisplayName]);

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    const validationError = contactEmailErrorMessage(email);
    if (validationError) {
      setEmailError(validationError);
      return;
    }
    setEmailError("");
    await onSubmit(email, displayName);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={CONTACT_DIALOG_TITLE_ID}
      sx={getGalleryInquiryDialogSx()}
      BackdropProps={{ sx: standardDialogBackdropSx }}
      PaperProps={{
        elevation: 0,
        sx: [
          getStandardDialogPaperSx(theme),
          getGalleryInquiryPaperSx(),
          { position: "relative" },
        ],
      }}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        size="small"
        sx={closeButtonSx}
      >
        <CloseIcon sx={{ fontSize: 20 }} />
      </IconButton>
      {saved ? (
        <Box sx={{ textAlign: "center" }}>
          <Box sx={galleryInquirySuccessIconWellSx}>
            <UploadCompleteIcon style={{ width: 26, height: 26 }} />
          </Box>
          <Typography
            id={CONTACT_DIALOG_TITLE_ID}
            variant="h6"
            sx={{ ...galleryInquiryTitleSx, mb: 1 }}
          >
            Email saved
          </Typography>
          <Typography sx={{ ...galleryInquiryBodySx, mb: 2 }}>
            Leads will be sent to{" "}
            <strong>{normalizeEmail(email) || "your email"}</strong>.
          </Typography>
          <Button
            type="button"
            variant="contained"
            onClick={onClose}
            disableElevation
            fullWidth
            autoFocus
            sx={primaryFullWidthCtaSx}
          >
            Done
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography
            id={CONTACT_DIALOG_TITLE_ID}
            variant="h6"
            sx={{ ...galleryInquiryTitleSx, ...galleryInquiryHeaderInsetSx, mb: 0.5 }}
          >
            Where should leads go?
          </Typography>
          <Typography sx={{ ...galleryInquiryBodySx, mb: 2 }}>
            When someone taps &quot;I&apos;m Interested&quot; on this plan, we&apos;ll email
            you the lead and the layout they explored.
          </Typography>
          <TextField
            fullWidth
            label="Email"
            placeholder="you@email.com"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(contactEmailErrorMessage(e.target.value));
            }}
            onBlur={() => {
              if (email.trim()) setEmailError(contactEmailErrorMessage(email));
            }}
            autoFocus={focusEmailOnOpen}
            autoComplete="email"
            error={Boolean(emailError)}
            helperText={emailError || undefined}
            InputLabelProps={{ shrink: true }}
            InputProps={{ style: { height: 48 } }}
            inputProps={{
              inputMode: "email",
              autoCapitalize: "none",
              autoCorrect: "off",
              spellCheck: false,
              enterKeyHint: "done",
            }}
            sx={{ mb: 2, ...emailFieldSx }}
          />
          {/*
            Optional, and left blank on save means "leave whatever is stored
            alone" rather than "clear it" — a plan already labelled elsewhere
            must not lose that label because this modal was reopened to correct
            the email.
          */}
          <TextField
            fullWidth
            label="Name shown in emails (optional)"
            placeholder="Property name or unit"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            helperText="How this plan is named in the emails. Leave blank to keep the current name."
            InputLabelProps={{ shrink: true }}
            InputProps={{ style: { height: 48 } }}
            inputProps={{
              maxLength: MAX_DISPLAY_NAME_CHARS,
              enterKeyHint: "done",
            }}
            sx={{ mb: 2, ...emailFieldSx }}
          />
          {error ? (
            <Typography
              role="alert"
              variant="body2"
              sx={{ color: theme.palette.error.main, mb: 1.5 }}
            >
              {error}
            </Typography>
          ) : null}
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !normalizeEmail(email)}
            disableElevation
            fullWidth
            sx={primaryFullWidthCtaSx}
          >
            {submitting ? "Saving…" : "Save email"}
          </Button>
          <AuthLegalStrip sx={{ pt: 1.5 }} />
        </Box>
      )}
    </Dialog>
  );
}

GalleryUploaderContactModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  accountEmail: PropTypes.string,
  savedContactEmail: PropTypes.string,
  savedInquiryDisplayName: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  saved: PropTypes.bool,
  error: PropTypes.string,
};
